import React, { useState, useEffect } from 'react';
import { Button } from 'components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card';
import { Badge } from 'components/ui/badge';
import { Label } from 'components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import { Checkbox } from 'components/ui/checkbox';
import api from 'services/api';

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
}

interface Document {
  id: string;
  filename: string;
  created_at: string;
  uploader_id: string;
}

interface QAPair {
  id: string;
  question: string;
  answer: string;
  question_type: string;
  document_id: string;
}

const TaskAssignmentPage: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [documents, setDocuments] = useState<Document[]>([]);
  const [selectedDocument, setSelectedDocument] = useState<string>('');
  const [selectedResearchers, setSelectedResearchers] = useState<string[]>([]);
  const [batchSize, setBatchSize] = useState<number>(10);
  const [loading, setLoading] = useState(true);
  const [assigning, setAssigning] = useState(false);
  const [assignmentResult, setAssignmentResult] = useState<any>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      // In a real implementation, these would be separate API calls
      // For now, we'll simulate the data

      // Simulate users fetch
      const mockUsers: User[] = [
        { id: '1', name: 'Alice Researcher', email: 'alice@example.com', role: 'researcher', status: 'active' },
        { id: '2', name: 'Bob Researcher', email: 'bob@example.com', role: 'researcher', status: 'active' },
        { id: '3', name: 'Charlie Researcher', email: 'charlie@example.com', role: 'researcher', status: 'active' },
        { id: '4', name: 'Diana Admin', email: 'diana@example.com', role: 'admin', status: 'active' }
      ];

      // Simulate documents fetch
      const mockDocuments: Document[] = [
        { id: 'doc1', filename: 'Company Handbook.pdf', created_at: '2023-01-15', uploader_id: '1' },
        { id: 'doc2', filename: 'Product Specifications.docx', created_at: '2023-02-20', uploader_id: '2' },
        { id: 'doc3', filename: 'Market Analysis.xlsx', created_at: '2023-03-10', uploader_id: '1' }
      ];

      setUsers(mockUsers);
      setDocuments(mockDocuments);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch data', error);
      setLoading(false);
    }
  };

  const handleResearcherToggle = (userId: string) => {
    setSelectedResearchers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleAssignTasks = async () => {
    if (!selectedDocument || selectedResearchers.length === 0) {
      alert('Please select a document and at least one researcher');
      return;
    }

    setAssigning(true);
    try {
      // In a real implementation, this would call the task assignment API
      // For now, we'll simulate the process
      await new Promise(resolve => setTimeout(resolve, 1500));

      const mockResult = {
        document_id: selectedDocument,
        batch_size: batchSize,
        assignments: selectedResearchers.reduce((acc, userId) => {
          const user = users.find(u => u.id === userId);
          if (user) {
            acc[user.name] = Array(Math.floor(batchSize / selectedResearchers.length)).fill(0).map((_, i) => `QA-${i + 1}`);
          }
          return acc;
        }, {} as Record<string, string[]>),
        message: `Successfully assigned ${batchSize} QA pairs to ${selectedResearchers.length} researchers`
      };

      setAssignmentResult(mockResult);
    } catch (error) {
      console.error('Failed to assign tasks', error);
    } finally {
      setAssigning(false);
    }
  };

  const researchers = users.filter(user => user.role?.toLowerCase() === 'researcher');

  if (loading) {
    return <div className="p-6">Loading data...</div>;
  }

  return (
    <div className="p-6">
      <h1 className="text-3xl font-bold text-text-primary">Task Assignment</h1>
      <p className="mt-2 text-text-secondary">
        Assign QA validation tasks to researchers.
      </p>

      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Assignment Form */}
        <Card>
          <CardHeader>
            <CardTitle>Create Validation Batch</CardTitle>
            <CardDescription>Assign QA pairs for validation</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label htmlFor="document">Select Document</Label>
              <Select value={selectedDocument} onValueChange={setSelectedDocument}>
                <SelectTrigger id="document">
                  <SelectValue placeholder="Select a document" />
                </SelectTrigger>
                <SelectContent>
                  {documents.map(doc => (
                    <SelectItem key={doc.id} value={doc.id}>
                      {doc.filename}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Batch Size</Label>
              <div className="flex items-center space-x-2">
                <input
                  type="range"
                  min="5"
                  max="100"
                  value={batchSize}
                  onChange={(e) => setBatchSize(parseInt(e.target.value))}
                  className="w-full"
                />
                <span className="w-12 text-center font-medium">{batchSize}</span>
              </div>
              <p className="text-sm text-text-secondary mt-1">
                Number of QA pairs to include in this validation batch
              </p>
            </div>

            <div>
              <Label>Select Researchers</Label>
              <div className="mt-2 space-y-2">
                {researchers.map(user => (
                  <div key={user.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={`researcher-${user.id}`}
                      checked={selectedResearchers.includes(user.id)}
                      onCheckedChange={() => handleResearcherToggle(user.id)}
                    />
                    <label
                      htmlFor={`researcher-${user.id}`}
                      className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      {user.name} <span className="text-text-secondary">({user.email})</span>
                    </label>
                  </div>
                ))}
              </div>
              {researchers.length === 0 && (
                <p className="text-sm text-text-secondary">No researchers available</p>
              )}
            </div>

            <Button
              onClick={handleAssignTasks}
              disabled={assigning || !selectedDocument || selectedResearchers.length === 0}
              className="w-full"
            >
              {assigning ? 'Assigning Tasks...' : 'Assign Validation Tasks'}
            </Button>
          </CardContent>
        </Card>

        {/* Researchers List */}
        <Card>
          <CardHeader>
            <CardTitle>Researchers</CardTitle>
            <CardDescription>Available researchers for task assignment</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {researchers.map(user => (
                <div key={user.id} className="flex items-center justify-between p-3 border border-border rounded-lg">
                  <div>
                    <h3 className="font-medium">{user.name}</h3>
                    <p className="text-sm text-text-secondary">{user.email}</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant={user.status === 'active' ? 'default' : 'secondary'}>
                      {user.status}
                    </Badge>
                    <Checkbox
                      checked={selectedResearchers.includes(user.id)}
                      onCheckedChange={() => handleResearcherToggle(user.id)}
                    />
                  </div>
                </div>
              ))}

              {researchers.length === 0 && (
                <p className="text-center text-text-secondary py-4">
                  No researchers found. Please ensure researchers have been added to the system.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Assignment Result */}
      {assignmentResult && (
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Assignment Result</CardTitle>
            <CardDescription>{assignmentResult.message}</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {Object.entries(assignmentResult.assignments).map(([researcher, qaPairs]) => (
                <div key={researcher} className="p-3 bg-surface border border-border rounded-lg">
                  <h3 className="font-medium">{researcher}</h3>
                  <p className="text-sm text-text-secondary">
                    Assigned {Array.isArray(qaPairs) ? qaPairs.length : 0} QA pairs
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1">
                    {Array.isArray(qaPairs) && qaPairs.slice(0, 10).map((pair: string, idx: number) => (
                      <Badge key={idx} variant="secondary">{pair}</Badge>
                    ))}
                    {Array.isArray(qaPairs) && qaPairs.length > 10 && (
                      <Badge variant="secondary">+{qaPairs.length - 10} more</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <Button className="mt-4" onClick={() => setAssignmentResult(null)}>
              Create Another Assignment
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Instructions */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle>How Task Assignment Works</CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="list-decimal list-inside space-y-2 text-text-secondary">
            <li>Select a document with generated QA pairs</li>
            <li>Choose the number of QA pairs to include in the validation batch</li>
            <li>Select researchers to assign the validation tasks to</li>
            <li>Click "Assign Validation Tasks" to distribute the workload</li>
            <li>Researchers will receive notifications about their new validation tasks</li>
          </ol>
          <p className="mt-4 text-text-secondary">
            The system automatically distributes QA pairs evenly among selected researchers.
            Each researcher will receive approximately {batchSize}/{selectedResearchers.length || 1} QA pairs.
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default TaskAssignmentPage;